import { IFilterModel, IGridPostModel } from "common/models/GridModels";
import { IAsyncDropdownModel } from "common/models/IDropdownModel";
import dayjs from "dayjs";
import { z } from "zod";

//#region  Material Type Wise Count
export interface IMaterialTypeWiseCount {
  materialTypeID: string | number;
  materialTypeName: string;
  materialCount: number;
  accCount: number;
}
//#endregion

export interface IGeneralCard {
  totalMaterial?: number;
  totalMaterialAccession?: number;
  totalMember?: number;
  totalMatAccessionIssueToday?: number;
  totalDueBookOverall?: number;
  totalUniqueVisitorsToday?: number;
  totalIssuedMatAccessionsOverAll?: number;
  totalMatAccessionReturnToday?: number;
}

//#region Visitor
export interface IVisitor {
  uniqueID: string | number;
  libraryName: string | number;
  date: Date;
  todaysVisitor: number;
  weekVisitor: number;
  monthVisitor: number;
}

export const IVisitorGridFilterSchema = z.object({
  memberTypeID: z.string().nullish(),
});

export type IVisitorGridFilter = z.infer<typeof IVisitorGridFilterSchema>;

//#endregion

//#region TitleVolume
export interface ITitleVolume {
  uniqueID: string | number;
  instituteCode: string;
  departmentCode: string;
  title: number;
  volume: number;
  totalPrice: number;
  totalTitle: number;
  totalVolume: number;
  allTotalPrice: number;
}

export const ITitleVolumeGridFilterSchema = z.object({
  materialTypeID: z.string().nullish(),
  instituteID: z.string().nullish(),
  departmentID: z.string().nullish(),
});

export type ITitleVolumeGridFilter = z.infer<
  typeof ITitleVolumeGridFilterSchema
>;

//#endregion

//#region Circulation
export interface ICirculation {
  uniqueID: string | number;
  materialTypeID: string | number;
  materialTypeName: string;
  todayIssueCount: number;
  todayReturnCount: number;
  lastWeekIssueCount: number;
  lastWeekReturnCount: number;
  last30DaysIssueCount: number;
  last30DaysReturnCount: number;
  filterDateIssueCount: number;
  filterDateReturnCount: number;
}

export const ICirculationGridFilterSchema = z.object({
  materialTypeID: z.string().nullish(),
  memberTypeID: z.string().nullish(),
  fromDate: z.coerce
    .string()
    .transform((c) => (c === "null" ? null : c))
    .transform((c) => (c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    }),
  toDate: z.coerce
    .string()
    .transform((c) => (c === "null" ? null : c))
    .transform((c) => (c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    }),
});

export type ICirculationGridFilter = z.infer<
  typeof ICirculationGridFilterSchema
>;

//#endregion

//#region ProgramWiseCirculation
export interface IProgramCirculation {
  uniqueID: string | number;
  materialTypeID: string | number;
  materialTypeName: string;
  todayCount: number;
  todayReturnCount: number;
  lastWeekCount: number;
  lastWeekReturnCount: number;
  last30DaysCount: number;
  last30DaysReturnCount: number;
  filterDateCount: number;
  filterDateReturnCount: number;
}

export const IProgramCirculationGridFilterSchema = z.object({
  programID: z.string().nullish(),
  memberTypeID: z.string().nullish(),
  fromDate: z.coerce
    .string()
    .transform((c) => (c === "null" ? null : c))
    .transform((c) => (c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    }),
  toDate: z.coerce
    .string()
    .transform((c) => (c === "null" ? null : c))
    .transform((c) => (c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    }),
});

export type IProgramCirculationGridFilter = z.infer<
  typeof IProgramCirculationGridFilterSchema
>;
//#endregion

//#region DueMaterials
export interface IDueMaterial {
  uniqueID: string | number | null;
  materialTypeID: string | number | null;
  memberType: string | null;
  materialType: string | null;
  institute: string | null;
  accessionNo: string | null;
  memberName: string | null;
  issueDate: Date | null;
  dueDate: Date | null;
  dueDay: number | null;
  fineAmount: number | null;
  title: string;
  authorName: string | null;
}

export const IDueMaterialGridFilterSchema = z.object({
  materialTypeID: z.string().nullish(),
  memberTypeID: z.string().nullish(),
  dueDays: z.coerce
    .number({ invalid_type_error: "Enter Valid Number" })
    .nonnegative({ message: "Number must be greater than or equal to 0" })
    .nullish(),
  remainingDays: z.coerce
    .number({ invalid_type_error: "Enter Valid Number" })
    .nonnegative({ message: "Number must be greater than or equal to 0" })
    .nullish(),
  fineAmount: z.coerce
    .number({ invalid_type_error: "Enter Valid Number" })
    .nonnegative({ message: "Number must be greater than or equal to 0" })
    .nullish(),
  fromDueDate: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    })
    .nullish(),
  toDueDate: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    })
    .nullish(),
  fromIssueDate: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    })
    .nullish(),
  toIssueDate: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    })
    .nullish(),
  materialAccessionNoID: z.string().nullish(),
  accessionNoValue: z.custom<IAsyncDropdownModel>().optional(),
  memberID: z.string().nullish(),
  memberCodeValue: z.custom<IAsyncDropdownModel>().optional(),
  dueStatus: z.coerce.number().nullish(),
});

export type IDueMaterialGridFilter = z.infer<
  typeof IDueMaterialGridFilterSchema
>;
//#endregion

//#region Issued Item Chart
export interface IIssuedItemChart {
  year: number;
  month: number;
  memberTypeCode: string;
  memberTypeName: string;
  yearMonthName: number;
  totalIssue: number;
}

export const IIssuedItemChartGridFilterSchema = z.object({
  actionType: z.coerce.number().nullish(),
  fromYear: z.coerce
    .string()
    .transform((c) => (c === "null" ? null : c))
    .transform((c) => (c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    }),
  toYear: z.coerce
    .string()
    .transform((c) => (c === "null" ? null : c))
    .transform((c) => (c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    }),
});

export type IIssuedItemChartGridFilter = z.infer<
  typeof IIssuedItemChartGridFilterSchema
>;

//#endregion

//#region Yearly Circulation
export interface IYearlyCirculation {
  uniqueID: string | number;
  libraryName: string;
  instituteName: string;
  year: number;
  totalIssue: number;
  totalReturn: number;
  totalRenew: number;
}
//#endregion

//#region Monthly Circulation
export interface IMonthlyCirculation {
  uniqueID: string | number;
  libraryName: string;
  instituteName: string;
  year: number;
  month: number;
  monthName: string;
  totalIssue: number;
  totalReturn: number;
  totalRenew: number;
}

export const IMonthlyCirculationGridFilterSchema = z.object({
  year: z.coerce.number().min(1900).max(2100),
  instituteID: z.string().nullish(),
});

export type IMonthlyCirculationGridFilter = z.infer<
  typeof IMonthlyCirculationGridFilterSchema
>;
//#endregion

export interface IMemberTypeChart {
  year: number;
  memberTypeCode: string;
  memberTypeName: string;
  totalMember: number;
}

export interface IMemberTypeChartGridFilter extends IFilterModel {
  fromYear: number | null | string;
  toYear: number | null | string;
}

export interface IMemberTypeChartGridPostModel extends IGridPostModel {
  filter?: IMemberTypeChartGridFilter;
}

//#region Recently Added Books
export interface IRecentlyAddedBook {
  materialID: string | number;
  materialCode: string;
  title: string;
  printTitle?: string;
  classificationCode?: string;
  classificationName?: string;
  authorNameCSV?: string;
  primaryAuthorName?: string;
  publicationName?: string;
  publicationYear?: string;
  edition?: string;
  isbn?: string;
  languageName?: string;
  pages?: string;
  size?: string;
  physicalMediumName?: string;
  materialTypeName?: string;
  instituteName?: string;
  departmentName?: string;
  accessionCount: string;
  accessionNoCSV?: string;
  price?: number;
  created: string;
}

export interface IRecentlyAddedBooksSummary {
  totalBooks: number;
  addedLast7Days: number;
  addedLast30Days: number;
  recentBooks: IRecentlyAddedBook[];
}

export const IRecentlyAddedBooksGridFilterSchema = z.object({
  materialTypeID: z.string().nullish(),
  instituteID: z.string().nullish(),
  departmentID: z.string().nullish(),
  languageID: z.string().nullish(),
  fromDate: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" || !c ? null : c))
    .nullable()
    .optional(),
  toDate: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" || !c ? null : c))
    .nullable()
    .optional(),
  searchText: z.coerce
    .string()
    .transform((c) => (c === "null" || c === "undefined" || !c ? null : c.trim()))
    .nullable()
    .optional(),
});

export type IRecentlyAddedBooksGridFilter = z.infer<
  typeof IRecentlyAddedBooksGridFilterSchema
>;
//#endregion

